import parse from 'html-react-parser';
import React from 'react';
import {
	Avatar as DefaultAvatar,
	useChannelActionContext,
	useComponentContext,
	useMessageContext,
	useTranslationContext,
} from 'stream-chat-react';

import DOMPurify from 'dompurify';

const sanitizeHtml = (message: string) => {
	const rawHtml = message.trim();
	const cleanHtml = DOMPurify.sanitize(rawHtml, {
		USE_PROFILES: { html: true },
		ALLOWED_TAGS: ['p', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'br', 'span', 'a'],
		ALLOWED_ATTR: ['href', 'target', 'rel'],
	});

	return parse(cleanHtml);
};

function CustomQuotedMessage() {
	const { Attachment, Avatar: ContextAvatar } = useComponentContext('QuotedMessage');
	const { isMyMessage, message } = useMessageContext('QuotedMessage');
	const { t, userLanguage } = useTranslationContext('QuotedMessage');
	const { jumpToMessage } = useChannelActionContext('QuotedMessage');

	const quotedMessage = message.quoted_message;

	if (!quotedMessage) {
		return null;
	}

	const quotedMessageDeleted = quotedMessage.deleted_at || quotedMessage.type === 'deleted';
	const quotedMessageText = quotedMessageDeleted
		? t('This message was deleted...')
		: quotedMessage.i18n?.[`${userLanguage}_text`] || quotedMessage.text;

	const quotedMessageAttachment =
		quotedMessage.attachments?.length && !quotedMessageDeleted
			? quotedMessage.attachments[0]
			: null;

	if (!quotedMessageText && !quotedMessageAttachment) {
		return null;
	}

	const Avatar = ContextAvatar || DefaultAvatar;
	const quotedMessageBody = quotedMessageText ? sanitizeHtml(quotedMessageText) : null;

	return (
		<>
			<div
				className={`str-chat__quoted-message-preview quoted-message${
					isMyMessage() ? ' mine' : ''
				}`}
				data-testid="quoted-message"
				onClickCapture={(event) => {
					event.stopPropagation();
					event.preventDefault();
					jumpToMessage(quotedMessage.id);
				}}
			>
				{quotedMessage.user && (
					<Avatar
						image={quotedMessage.user.image}
						name={quotedMessage.user.name || quotedMessage.user.id}
						size={20}
						user={quotedMessage.user}
					/>
				)}
				<div
					className="quoted-message-inner str-chat__quoted-message-bubble"
					data-testid="quoted-message-contents"
				>
					{quotedMessageAttachment && (
						<Attachment attachments={[quotedMessageAttachment]} isQuoted />
					)}
					<div data-testid="quoted-message-text">{quotedMessageBody || quotedMessageText}</div>
				</div>
			</div>
			{message.attachments?.length ? <Attachment attachments={message.attachments} /> : null}
		</>
	);
}

export default CustomQuotedMessage;
